<script lang="ts">
  import { app, persistSettings } from './store.svelte';
  import { t } from './i18n';
  import { buildVaseMesh, VASE_PRESETS, type VaseParams, type TextureGrid } from '../core/vaseModel';
  import { centerOnPlate } from '../core/mesh';
  import { exportBinaryStl } from '../core/stlExport';
  import { imageToTextureGrid } from './imageTexture';
  import { BUILTIN_PATTERNS, svgToTextureGrid, patternThumbnailDataUrl } from './patterns';
  import { rafDebounce } from './rafDebounce';
  import { activeVase, updateActiveVaseUniforms, updateActiveVaseTexture } from './gpuVaseMaterial';
  import { vectorizeFile, QUALITY_PRESETS, type VectorizeMode, type Quality } from './vectorize';
  import NumberSlider from './NumberSlider.svelte';
  import Icon from './lib/Icon.svelte';

  let textureFile: HTMLInputElement | undefined = $state();
  let textureIntensity = $state(2);
  let textureRepeatU = $state(2);
  let textureRepeatV = $state(1);
  let texturePreviewUrl = $state<string | null>(null);

  // Vectorization state — when user uploads a raster, we open this mini panel
  // to let them tune threshold/mode and re-vectorize before applying.
  let vectorizeOpen = $state(false);
  let vectorizeSource = $state<File | null>(null);
  let vectorizeQuality = $state<Quality>('high');
  let vectorizeMode = $state<VectorizeMode>('monochrome');
  let vectorizeThreshold = $state(0.5);
  let vectorizeLevels = $state(3);
  let vectorizeBlur = $state(2);
  let vectorizeSmoothing = $state(2);
  let vectorizeSimplify = $state(0.3);
  let vectorizeBezier = $state(true);
  let vectorizeWorkingSize = $state(320);
  let vectorizeInvert = $state(false);
  let vectorizedSvg = $state<string | null>(null);
  let vectorizing = $state(false);

  function applyQualityPreset(q: Quality) {
    vectorizeQuality = q;
    const p = QUALITY_PRESETS[q];
    vectorizeWorkingSize = p.workingSize;
    vectorizeBlur = p.blur;
    vectorizeSmoothing = p.smoothing;
    vectorizeSimplify = p.simplify;
    vectorizeBezier = p.bezier;
    debouncedRevectorize();
  }

  const RASTER_EXT = /\.(png|jpe?g|webp|gif|bmp)$/i;

  // Vase mesh rebuild is now centralized in Viewer.svelte ($effect on vaseParams).
  // Calling app.vaseParams = ... here is enough — the viewer rebuilds via rAF debounce.
  const scheduleRebuild = (): void => {
    app.fileName = `vase-${app.vaseParams.bottomRadius.toFixed(0)}x${app.vaseParams.height.toFixed(0)}.gen`;
  };

  /** Keys that drive the legacy 3-point silhouette. Editing any of these implicitly
   *  switches OFF Pen-profile mode (otherwise penProfile would mask the change and
   *  the slider would appear inert). */
  const SHAPE_KEYS: ReadonlySet<keyof VaseParams> = new Set([
    'height', 'bottomRadius', 'bellyRadius', 'bellyHeight', 'topRadius',
    'twist', 'noiseAmplitude', 'noiseFrequency'
  ]);

  function update<K extends keyof VaseParams>(key: K, value: VaseParams[K]) {
    const patch: Partial<VaseParams> = { [key]: value };
    if (SHAPE_KEYS.has(key) && app.vaseParams.penProfile) {
      // User is sculpting via legacy sliders → clear pen override so it actually shows.
      patch.penProfile = null;
    }
    app.vaseParams = { ...app.vaseParams, ...patch };
    scheduleRebuild();
  }

  function applyPreset(presetId: string) {
    const preset = VASE_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    // Clear BOTH override layers — legacy profilePoints AND new penProfile —
    // otherwise the preset's bottom/belly/top values would be masked by either
    // override path inside effectiveProfile().
    app.vaseParams = {
      ...app.vaseParams,
      ...preset.params,
      profilePoints: undefined,
      penProfile: null
    };
    scheduleRebuild();
  }

  async function onTextureChosen(e: Event) {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    // SVG → use directly. Raster (PNG/JPG/...) → open vectorize panel for tuning.
    if (file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
      const text = await file.text();
      lastSvgSource = text;
      autoBumpTessellation();
      const grid = await svgToTextureGrid(text, textureIntensity, textureRepeatU, textureRepeatV, app.textureResolution);
      if (texturePreviewUrl) URL.revokeObjectURL(texturePreviewUrl);
      texturePreviewUrl = URL.createObjectURL(file);
      app.vaseParams = { ...app.vaseParams, texture: grid };
      if (!updateActiveVaseTexture(grid)) scheduleRebuild();
    } else if (RASTER_EXT.test(file.name) || file.type.startsWith('image/')) {
      vectorizeSource = file;
      vectorizeOpen = true;
      await runVectorize();
    } else {
      // Fallback — treat as raw raster luminance (no vectorization)
      const grid = await imageToTextureGrid(file, textureIntensity, textureRepeatU, textureRepeatV);
      app.vaseParams = { ...app.vaseParams, texture: grid };
      if (!updateActiveVaseTexture(grid)) scheduleRebuild();
    }
    // Reset the file input so re-selecting the same file fires onchange again
    if (textureFile) textureFile.value = '';
  }

  async function runVectorize() {
    if (!vectorizeSource) return;
    vectorizing = true;
    try {
      vectorizedSvg = await vectorizeFile(vectorizeSource, {
        mode: vectorizeMode,
        workingSize: vectorizeWorkingSize,
        threshold: vectorizeThreshold,
        posterizeLevels: vectorizeLevels,
        blur: vectorizeBlur,
        simplify: vectorizeSimplify,
        smoothing: vectorizeSmoothing,
        bezier: vectorizeBezier,
        invert: vectorizeInvert
      });
    } catch (e) {
      console.error('vectorize failed', e);
      vectorizedSvg = null;
    } finally {
      vectorizing = false;
    }
  }

  async function applyVectorized() {
    if (!vectorizedSvg) return;
    lastSvgSource = vectorizedSvg;
    autoBumpTessellation();
    const grid = await svgToTextureGrid(vectorizedSvg, textureIntensity, textureRepeatU, textureRepeatV, app.textureResolution);
    if (texturePreviewUrl) URL.revokeObjectURL(texturePreviewUrl);
    texturePreviewUrl = patternThumbnailDataUrl(vectorizedSvg);
    app.vaseParams = { ...app.vaseParams, texture: grid };
    if (!updateActiveVaseTexture(grid)) scheduleRebuild();
    closeVectorize();
  }

  function downloadVectorized() {
    if (!vectorizedSvg) return;
    const blob = new Blob([vectorizedSvg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (vectorizeSource?.name?.replace(/\.[^.]+$/, '') ?? 'vectorized') + '.svg';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  function closeVectorize() {
    vectorizeOpen = false;
    vectorizeSource = null;
    vectorizedSvg = null;
  }

  // Re-rasterize the current SVG texture source at the new resolution.
  // Only needed when the user changes textureResolution after a texture is already applied.
  let lastSvgSource: string | null = $state(null);
  async function reapplyCurrentTexture() {
    if (!lastSvgSource) return;
    const grid = await svgToTextureGrid(lastSvgSource, textureIntensity, textureRepeatU, textureRepeatV, app.textureResolution);
    app.vaseParams = { ...app.vaseParams, texture: grid };
    if (!updateActiveVaseTexture(grid)) scheduleRebuild();
  }

  // Re-run vectorize when any tuning knob changes (debounced)
  const debouncedRevectorize = rafDebounce(() => runVectorize());

  function clearTexture() {
    if (texturePreviewUrl) URL.revokeObjectURL(texturePreviewUrl);
    texturePreviewUrl = null;
    app.vaseParams = { ...app.vaseParams, texture: null };
    scheduleRebuild();
  }

  async function applyPattern(pattern: { id: string; name: string; svg: string }) {
    lastSvgSource = pattern.svg;
    autoBumpTessellation();
    const grid = await svgToTextureGrid(pattern.svg, textureIntensity, textureRepeatU, textureRepeatV, app.textureResolution);
    if (texturePreviewUrl) URL.revokeObjectURL(texturePreviewUrl);
    texturePreviewUrl = patternThumbnailDataUrl(pattern.svg);
    app.vaseParams = { ...app.vaseParams, texture: grid };
    if (!updateActiveVaseTexture(grid)) {
      scheduleRebuild();
    }
  }

  /** Texture displacement quality is limited by mesh density: at 96×80 verts the
   * displacement aliases into visible voxel-stepping. Bump to at least 192×160 when
   * a texture is applied so smooth gradients in the texture become smooth bumps. */
  function autoBumpTessellation() {
    const minSeg = 192, minRing = 160;
    if (app.vaseParams.segments < minSeg || app.vaseParams.rings < minRing) {
      app.vaseParams = {
        ...app.vaseParams,
        segments: Math.max(app.vaseParams.segments, minSeg),
        rings: Math.max(app.vaseParams.rings, minRing)
      };
    }
  }

  /** Texture knob slider — tries GPU uniform path first; falls back to CPU rebuild. */
  function updateTextureKnob<K extends keyof TextureGrid>(key: K, value: TextureGrid[K]) {
    if (!app.vaseParams.texture) return;
    app.vaseParams = {
      ...app.vaseParams,
      texture: { ...app.vaseParams.texture, [key]: value }
    };
    if (activeVase.material) {
      const t = app.vaseParams.texture;
      if (!t) return;
      // INSTANT path — uniform write only, no JS work for any texture knob.
      updateActiveVaseUniforms({
        intensity: t.intensity,
        repeatU: t.repeatU,
        repeatV: t.repeatV,
        zMin: t.zMin,
        zMax: t.zMax,
        falloffBottom: t.falloffBottom,
        falloffTop: t.falloffTop,
        falloffCurve: t.falloffCurve,
        surfaceSmoothing: t.surfaceSmoothing
      });
    } else {
      scheduleRebuild();
    }
  }

  /** SVG Paint helpers — restrict the texture to a Z band. */
  function useFullRange() {
    if (!app.vaseParams.texture) return;
    app.vaseParams = {
      ...app.vaseParams,
      texture: { ...app.vaseParams.texture, zMin: 0, zMax: 1 }
    };
  }

  /** True when the user has selected a pen-anchor range (≥ 2 anchors with z values
   *  on the active penProfile) that we can map onto a Z band. */
  let penAnchorRangeAvailable = $derived.by(() => {
    const pen = app.vaseParams.penProfile;
    return !!(pen && pen.anchors.length >= 2);
  });

  /** Map currently-selected pen anchors (or fall back to first/last) to a normalized
   *  Z band. Reads selection from window.__clayforgePenSelection if available. */
  function useSelectedAnchorRange() {
    if (!app.vaseParams.texture || !app.vaseParams.penProfile) return;
    const anchors = app.vaseParams.penProfile.anchors;
    const sel = (window as unknown as { __clayforgePenSelection?: number[] }).__clayforgePenSelection;
    let z0: number, z1: number;
    if (sel && sel.length >= 2) {
      const zs = sel.map((i) => anchors[i]?.z).filter((z): z is number => typeof z === 'number');
      z0 = Math.min(...zs); z1 = Math.max(...zs);
    } else if (sel && sel.length === 1 && anchors.length >= 2) {
      // Single selection: pick the segment ending at that anchor.
      const i = sel[0];
      z0 = anchors[Math.max(0, i - 1)].z;
      z1 = anchors[i].z;
    } else {
      // No selection — full range.
      z0 = anchors[0].z; z1 = anchors[anchors.length - 1].z;
    }
    let H = 0;
    for (const a of anchors) if (a.z > H) H = a.z;
    if (H < 1e-6) return;
    const zMin = Math.max(0, Math.min(1, z0 / H));
    const zMax = Math.max(0, Math.min(1, z1 / H));
    app.vaseParams = {
      ...app.vaseParams,
      texture: { ...app.vaseParams.texture, zMin: Math.min(zMin, zMax), zMax: Math.max(zMin, zMax) }
    };
  }

  function exportStl() {
    if (app.triangles.length === 0) return;
    const blob = exportBinaryStl(app.triangles);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (app.fileName || 'vase').replace(/\.(gen|stl)$/i, '') + '.stl';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  // Build immediately when this panel mounts and there's no model yet.
  $effect(() => {
    if (app.showVasePanel && app.triangles.length === 0) scheduleRebuild();
  });
</script>

<section class="vp">
  <div class="hd">
    <h3><Icon name="aperture" size={12} /> {t(app.lang, 'panel.vase')}</h3>
    <span class="badge"><Icon name="zap" size={9} /> GPU</span>
  </div>

  <label class="preset-row">
    <span>Hazır Şekil</span>
    <select onchange={(e) => applyPreset((e.currentTarget as HTMLSelectElement).value)} value="">
      <option value="" disabled>— Seç —</option>
      {#each VASE_PRESETS as p (p.id)}
        <option value={p.id}>{p.name}</option>
      {/each}
    </select>
  </label>

  <details open>
    <summary>Şekil</summary>
    <div class="grid2">
      <NumberSlider label="Yükseklik" unit="mm" min={20} max={300} step={1}
        value={app.vaseParams.height} onInput={(v) => update('height', v)} />
      <NumberSlider label="Taban Yarıçap" unit="mm" min={5} max={100} step={0.5}
        value={app.vaseParams.bottomRadius} onInput={(v) => update('bottomRadius', v)} />
      <NumberSlider label="Göbek Yarıçap" unit="mm" min={5} max={120} step={0.5}
        value={app.vaseParams.bellyRadius} onInput={(v) => update('bellyRadius', v)} />
      <NumberSlider label="Göbek Konumu" unit="0–1" min={0.05} max={0.95} step={0.01}
        value={app.vaseParams.bellyHeight} onInput={(v) => update('bellyHeight', v)} />
      <NumberSlider label="Tepe Yarıçap" unit="mm" min={2} max={100} step={0.5}
        value={app.vaseParams.topRadius} onInput={(v) => update('topRadius', v)} />
      <NumberSlider label="Burulma" unit="°" min={-360} max={360} step={1}
        value={app.vaseParams.twist} onInput={(v) => update('twist', v)} />
    </div>
  </details>

  <details>
    <summary>Tessellation & Duvar</summary>
    <div class="grid2">
      <NumberSlider label="Çevre Segment" unit="adet" min={8} max={256} step={4}
        value={app.vaseParams.segments} onInput={(v) => update('segments', Math.round(v))} />
      <NumberSlider label="Yükseklik Halka" unit="adet" min={8} max={256} step={2}
        value={app.vaseParams.rings} onInput={(v) => update('rings', Math.round(v))} />
      <NumberSlider label="Duvar Kalınlığı" unit="mm" min={0} max={8} step={0.1}
        value={app.vaseParams.wallThickness} onInput={(v) => update('wallThickness', v)} />
      <NumberSlider label="Taban Kalınlığı" unit="mm" min={0.5} max={10} step={0.1}
        value={app.vaseParams.bottomThickness} onInput={(v) => update('bottomThickness', v)} />
      <NumberSlider label="Yüzey Dalga" unit="mm" min={0} max={10} step={0.1}
        value={app.vaseParams.noiseAmplitude} onInput={(v) => update('noiseAmplitude', v)} />
      <NumberSlider label="Dalga Sıklığı" unit="adet" min={2} max={32} step={1}
        value={app.vaseParams.noiseFrequency} onInput={(v) => update('noiseFrequency', Math.round(v))} />
    </div>
  </details>

  <details open>
    <summary>Doku <span class="gpu-tag">GPU</span></summary>
    <input type="file" accept="image/*" bind:this={textureFile}
      onchange={onTextureChosen} style="display:none" />
    <div class="tex-row">
      {#if texturePreviewUrl}
        <img src={texturePreviewUrl} alt="texture" class="tex-thumb" />
      {:else}
        <button class="tex-add" onclick={() => textureFile?.click()}>+</button>
      {/if}
      <div class="tex-actions">
        <button class="mini" onclick={() => textureFile?.click()}>Yükle</button>
        {#if app.vaseParams.texture}
          <button class="mini" onclick={clearTexture}>Temizle</button>
        {/if}
      </div>
    </div>

    {#if vectorizeOpen}
      <div class="vec-panel">
        <div class="vec-head">
          <span class="ms small">auto_fix_high</span>
          <span class="vec-title">PNG/JPG → SVG Vektörleştir</span>
          <button class="vec-close" onclick={closeVectorize} aria-label="kapat">×</button>
        </div>

        <div class="vec-body">
          <div class="vec-preview">
            {#if vectorizing}
              <span class="ms spin">progress_activity</span>
            {:else if vectorizedSvg}
              {@html vectorizedSvg}
            {/if}
          </div>

          <div class="vec-controls">
            <div class="vec-row">
              <span class="vec-lab">Kalite</span>
              <div class="vec-chips">
                <button class:active={vectorizeQuality === 'low'}    onclick={() => applyQualityPreset('low')}>Hızlı</button>
                <button class:active={vectorizeQuality === 'medium'} onclick={() => applyQualityPreset('medium')}>Orta</button>
                <button class:active={vectorizeQuality === 'high'}   onclick={() => applyQualityPreset('high')}>Yüksek</button>
                <button class:active={vectorizeQuality === 'ultra'}  onclick={() => applyQualityPreset('ultra')}>Ultra</button>
              </div>
            </div>
            <div class="vec-row">
              <span class="vec-lab">Mod</span>
              <div class="vec-chips">
                <button class:active={vectorizeMode === 'monochrome'} onclick={() => { vectorizeMode = 'monochrome'; debouncedRevectorize(); }}>Mono</button>
                <button class:active={vectorizeMode === 'posterize'}  onclick={() => { vectorizeMode = 'posterize'; debouncedRevectorize(); }}>Poster</button>
                <button class:active={vectorizeMode === 'pixel'}      onclick={() => { vectorizeMode = 'pixel'; debouncedRevectorize(); }}>Pixel</button>
              </div>
            </div>

            {#if vectorizeMode === 'monochrome'}
              <NumberSlider label="Eşik" min={0.05} max={0.95} step={0.01} accent="#2bb673"
                value={vectorizeThreshold}
                onInput={(v) => { vectorizeThreshold = v; debouncedRevectorize(); }} />
            {/if}
            {#if vectorizeMode === 'posterize' || vectorizeMode === 'pixel'}
              <NumberSlider label="Seviye" min={2} max={6} step={1} accent="#2bb673"
                value={vectorizeLevels}
                onInput={(v) => { vectorizeLevels = Math.round(v); debouncedRevectorize(); }} />
            {/if}

            <NumberSlider label="Çözünürlük" unit="px" min={64} max={512} step={16} accent="#2bb673"
              value={vectorizeWorkingSize}
              onInput={(v) => { vectorizeWorkingSize = Math.round(v); debouncedRevectorize(); }} />
            <NumberSlider label="Bulanıklaştır" min={0} max={3} step={1} accent="#2bb673"
              value={vectorizeBlur}
              onInput={(v) => { vectorizeBlur = Math.round(v); debouncedRevectorize(); }} />
            <NumberSlider label="Yumuşatma" min={0} max={3} step={1} accent="#2bb673"
              value={vectorizeSmoothing}
              onInput={(v) => { vectorizeSmoothing = Math.round(v); debouncedRevectorize(); }} />
            <NumberSlider label="Sadeleştirme" min={0} max={2} step={0.05} accent="#2bb673"
              value={vectorizeSimplify}
              onInput={(v) => { vectorizeSimplify = v; debouncedRevectorize(); }} />

            <div class="vec-toggles">
              <label class="vec-toggle">
                <input type="checkbox" checked={vectorizeBezier}
                  onchange={(e) => { vectorizeBezier = (e.currentTarget as HTMLInputElement).checked; debouncedRevectorize(); }} />
                <span>Bézier Eğri</span>
              </label>
              <label class="vec-toggle">
                <input type="checkbox" checked={vectorizeInvert}
                  onchange={(e) => { vectorizeInvert = (e.currentTarget as HTMLInputElement).checked; debouncedRevectorize(); }} />
                <span>Tersle</span>
              </label>
            </div>
          </div>
        </div>

        <div class="vec-actions">
          <button class="ghost" onclick={downloadVectorized} disabled={!vectorizedSvg}>
            <span class="ms small">download</span> .svg
          </button>
          <button class="primary" onclick={applyVectorized} disabled={!vectorizedSvg || vectorizing}>
            <span class="ms small">brush</span> Vazoya Uygula
          </button>
        </div>
      </div>
    {/if}

    <div class="pg-grid">
      {#each BUILTIN_PATTERNS as p (p.id)}
        <button class="pg-card" onclick={() => applyPattern(p)} title={p.name}>
          <img src={patternThumbnailDataUrl(p.svg)} alt={p.name} />
        </button>
      {/each}
      <button class="pg-card ai" onclick={() => (app.showSvgGenerator = true)} title="AI ile SVG üret">
        <Icon name="sparkles" size={20} />
      </button>
    </div>

    {#if app.vaseParams.texture}
      <div class="push-pull-row">
        <span class="muted">Yön:</span>
        <button class="mini" class:active={(app.vaseParams.texture.intensity ?? 0) >= 0}
          onclick={() => updateTextureKnob('intensity', Math.abs(app.vaseParams.texture!.intensity))}>
          ▲ Pull (dışa)
        </button>
        <button class="mini" class:active={(app.vaseParams.texture.intensity ?? 0) < 0}
          onclick={() => updateTextureKnob('intensity', -Math.abs(app.vaseParams.texture!.intensity))}>
          ▼ Push (içe)
        </button>
      </div>
      <div class="grid2">
        <NumberSlider label="Yoğunluk (mutlak)" unit="mm" min={0.1} max={15} step={0.1}
          value={Math.abs(app.vaseParams.texture.intensity)}
          onInput={(v) => updateTextureKnob('intensity', (app.vaseParams.texture!.intensity < 0 ? -1 : 1) * v)} />
        <NumberSlider label="Yatay Tekrar" unit="U" min={1} max={16} step={1}
          value={app.vaseParams.texture.repeatU} onInput={(v) => updateTextureKnob('repeatU', Math.round(v))} />
        <div class="span2">
          <NumberSlider label="Dikey Tekrar" unit="V" min={1} max={16} step={1}
            value={app.vaseParams.texture.repeatV} onInput={(v) => updateTextureKnob('repeatV', Math.round(v))} />
        </div>
      </div>

      <div class="section-title" style="margin-top:14px"><Icon name="brush" size={11} /> Z-Bant Maskesi (SVG Paint)</div>
      <div class="grid2">
        <NumberSlider label="Bant Alt" unit="" min={0} max={1} step={0.01}
          value={app.vaseParams.texture.zMin ?? 0} onInput={(v) => updateTextureKnob('zMin', v)} />
        <NumberSlider label="Bant Üst" unit="" min={0} max={1} step={0.01}
          value={app.vaseParams.texture.zMax ?? 1} onInput={(v) => updateTextureKnob('zMax', v)} />
        <NumberSlider label="Alt Falloff" unit="" min={0} max={0.5} step={0.005}
          value={app.vaseParams.texture.falloffBottom ?? 0.02} onInput={(v) => updateTextureKnob('falloffBottom', v)} />
        <NumberSlider label="Üst Falloff" unit="" min={0} max={0.5} step={0.005}
          value={app.vaseParams.texture.falloffTop ?? 0.02} onInput={(v) => updateTextureKnob('falloffTop', v)} />
        <div class="span2">
          <NumberSlider label="Falloff Eğrisi" unit="" min={0.2} max={4} step={0.1}
            value={app.vaseParams.texture.falloffCurve ?? 1} onInput={(v) => updateTextureKnob('falloffCurve', v)} />
        </div>
        <div class="span2">
          <NumberSlider label="Yüzey Yumuşatma" unit="px" min={1} max={20} step={0.5}
            value={app.vaseParams.texture.surfaceSmoothing ?? 5} onInput={(v) => updateTextureKnob('surfaceSmoothing', v)} />
        </div>
      </div>
      <div class="paint-actions">
        <button class="mini" onclick={() => useFullRange()}
          title="Bantı tüm yüksekliğe aç (varsayılan)">Tümü</button>
        <button class="mini" onclick={() => useSelectedAnchorRange()}
          disabled={!penAnchorRangeAvailable}
          title="Pen profilinde seçili anchor aralığını bant olarak kullan (varsa)">Pen seçimini al</button>
      </div>
    {/if}

    <div class="texres-row">
      <NumberSlider label="Doku Çözünürlüğü" unit="px" min={64} max={512} step={16}
        value={app.textureResolution} onInput={(v) => { app.textureResolution = Math.round(v); reapplyCurrentTexture(); }} />
      <p class="hint">Tüm SVG kaynakları (hazır desenler dahil) için displacement netliği. Yüksek değer = keskin kabartma + daha fazla GPU.</p>
    </div>
  </details>

  <div class="actions">
    <button class="ghost" onclick={exportStl}>STL</button>
    <button class="primary" onclick={() => scheduleRebuild()}>{t(app.lang, 'vase.update')}</button>
  </div>
</section>

<style>
  .vp {
    display: flex; flex-direction: column; gap: 8px;
    height: 100%; overflow-y: auto; padding-right: 2px;
  }
  .color-row {
    display: flex; gap: 10px; padding: 8px 0;
    border-top: 1px dashed var(--border-dashed); margin-top: 4px;
  }
  .color-row label {
    flex: 1; display: flex; align-items: center; justify-content: space-between;
    gap: 6px; font-size: 11px; color: var(--text-2);
  }
  .color-row input[type="color"] {
    width: 32px; height: 22px; border: 1px solid var(--border-soft);
    border-radius: 4px; padding: 0; background: transparent; cursor: pointer;
  }
  .hd { display: flex; align-items: center; justify-content: space-between; }
  h3 {
    margin: 0; font-size: 10px; color: var(--text-3);
    text-transform: uppercase; letter-spacing: 0.1em; font-weight: 600;
    display: inline-flex; align-items: center; gap: 6px;
  }
  .badge {
    display: inline-flex; align-items: center; gap: 4px;
    background: linear-gradient(135deg, #1f8b4c, #34d399);
    color: white; font-size: 9px; padding: 3px 8px; border-radius: 5px;
    font-weight: 700; letter-spacing: 0.06em;
    box-shadow: 0 2px 8px -2px rgba(52, 211, 153, 0.4);
  }
  details {
    border: 1px solid var(--border-soft); border-radius: 8px;
    background: var(--panel-overlay-strong);
  }
  details > summary {
    list-style: none; cursor: pointer;
    padding: 7px 10px; font-size: 11px; font-weight: 500; color: var(--text-2);
    user-select: none;
  }
  details > summary::-webkit-details-marker { display: none; }
  details > summary::before {
    content: '▸'; display: inline-block; width: 12px; color: var(--text-muted);
    transition: transform 0.15s;
  }
  details[open] > summary::before { transform: rotate(90deg); }
  details > *:not(summary) { padding: 4px 10px 10px; }
  .gpu-tag {
    display: inline-block; margin-left: 6px;
    background: rgba(43,182,115,0.18); color: #2bb673;
    font-size: 8px; padding: 1px 5px; border-radius: 3px; font-weight: 600;
    letter-spacing: 0.05em;
  }

  .grid2 {
    display: grid; grid-template-columns: 1fr 1fr;
    column-gap: 14px; row-gap: 10px;
  }
  .grid2 label.span2 { grid-column: 1 / 3; }
  /* Each cell now uses 2 rows: header row (label + value) + slider full-width */
  label {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto;
    column-gap: 6px; row-gap: 3px;
    font-size: 11px; color: var(--text-3);
  }
  label .lab { grid-column: 1 / 2; grid-row: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  label .lab em {
    color: var(--text-muted); font-size: 9px; font-style: normal;
    margin-left: 4px; padding: 1px 5px;
    background: var(--chip-bg); border-radius: 3px;
    letter-spacing: 0.04em;
  }
  label output {
    grid-column: 2 / 3; grid-row: 1;
    color: #bfe6ff; font-weight: 600;
    font-variant-numeric: tabular-nums; min-width: 32px; text-align: right;
  }
  label input[type='range'] {
    grid-column: 1 / 3; grid-row: 2;
    width: 100%; accent-color: var(--accent); height: 18px;
    margin: 0;
  }

  .preset-row { display: flex; gap: 8px; align-items: center; font-size: 11px; }
  .preset-row span { color: var(--text-3); min-width: 70px; }
  .preset-row select {
    flex: 1; padding: 5px 8px;
    background: var(--chip-bg); color: var(--text-bright);
    border: 1px solid var(--border-soft); border-radius: 6px;
    font-size: 11px; font-family: inherit;
  }
  .preset-row select:focus { outline: none; border-color: var(--accent); }

  .tex-row { display: flex; gap: 8px; align-items: center; }
  .tex-thumb {
    width: 48px; height: 48px; object-fit: cover; border-radius: 6px;
    border: 1px solid var(--border-soft);
  }
  .tex-add {
    width: 48px; height: 48px;
    background: var(--chip-bg); border: 1px dashed var(--border-dashed);
    border-radius: 6px; color: var(--text-muted); font-size: 22px; cursor: pointer;
    transition: border-color 0.12s, color 0.12s;
  }
  .tex-add:hover { border-color: var(--accent); color: var(--accent); }
  .push-pull-row { display: flex; gap: 6px; align-items: center; padding: 6px 0; }
  .push-pull-row .muted { color: var(--text-muted); font-size: 11px; }
  .push-pull-row .mini { padding: 4px 10px; font-size: 11px; }
  .push-pull-row .mini.active { background: var(--accent); color: white; border-color: var(--accent); }
  .paint-actions { display: flex; gap: 6px; padding: 6px 0; }
  .paint-actions .mini { padding: 4px 10px; font-size: 11px; }
  .paint-actions .mini:disabled { opacity: 0.4; cursor: not-allowed; }
  .tex-actions { display: flex; flex-direction: column; gap: 4px; }
  .mini {
    background: var(--chip-bg); color: var(--text-2);
    border: 1px solid var(--border-soft);
    padding: 3px 8px; border-radius: 6px;
    cursor: pointer; font-size: 10.5px; font-family: inherit;
    transition: background 0.12s;
  }
  .mini:hover { background: var(--chip-bg-hover); color: var(--text-bright); }

  .pg-grid {
    display: grid; grid-template-columns: repeat(6, 1fr); gap: 4px;
    margin-top: 8px;
  }
  .pg-card {
    background: var(--chip-bg); border: 1px solid var(--border-soft); border-radius: 6px;
    padding: 2px; cursor: pointer; aspect-ratio: 1;
    transition: border-color 0.12s, transform 0.08s;
  }
  .pg-card:hover { border-color: var(--accent); transform: scale(1.03); }
  .pg-card:active { transform: scale(0.97); }
  .pg-card img {
    width: 100%; height: 100%; object-fit: cover;
    border-radius: 4px; background: #4a6783; display: block;
  }

  .pg-card.ai {
    background: linear-gradient(135deg, rgba(26, 122, 201, 0.4), rgba(43, 182, 115, 0.4));
    border-color: rgba(26, 122, 201, 0.5);
    display: grid; place-items: center;
    color: #bfe6ff;
  }
  .pg-card.ai .ms { font-size: 22px; }
  .pg-card.ai:hover {
    background: linear-gradient(135deg, rgba(26, 122, 201, 0.6), rgba(43, 182, 115, 0.6));
  }

  .actions { display: flex; gap: 6px; margin-top: 6px; }

  .span2 { grid-column: 1 / 3; }
  .texres-row {
    margin-top: 8px;
    padding: 8px;
    background: var(--panel-overlay-strong);
    border: 1px solid var(--border-soft);
    border-radius: 6px;
    display: flex; flex-direction: column; gap: 4px;
  }
  .texres-row .hint {
    font-size: 9.5px; color: var(--text-muted); margin: 0; line-height: 1.4;
  }

  /* ===== Vectorize panel (raster → SVG) ===== */
  .vec-panel {
    background: linear-gradient(180deg, rgba(43, 182, 115, 0.06), transparent);
    border: 1px solid rgba(43, 182, 115, 0.25);
    border-radius: 8px;
    padding: 8px;
    margin-top: 6px;
    display: flex; flex-direction: column; gap: 6px;
  }
  .vec-head {
    display: flex; align-items: center; gap: 6px;
    padding-bottom: 4px; border-bottom: 1px dashed var(--border-dashed);
  }
  .vec-head .ms { color: #2bb673; }
  .vec-title {
    font-size: 11px; font-weight: 600;
    color: var(--text-bright); flex: 1;
  }
  .vec-close {
    background: transparent; border: none; color: var(--text-muted);
    cursor: pointer; font-size: 16px; line-height: 1; padding: 0 4px;
  }
  .vec-close:hover { color: var(--text-bright); }

  .vec-body { display: grid; grid-template-columns: 92px 1fr; gap: 8px; }
  .vec-preview {
    aspect-ratio: 1; background: var(--bg-viewer-from);
    border-radius: 6px; border: 1px solid var(--border-soft);
    display: grid; place-items: center; overflow: hidden;
  }
  .vec-preview :global(svg) { width: 100%; height: 100%; }
  .vec-preview .ms.spin { color: var(--accent); font-size: 24px; }

  .vec-controls { display: flex; flex-direction: column; gap: 5px; min-width: 0; }
  .vec-toggles { display: flex; gap: 12px; margin-top: 2px; }
  .vec-row { display: flex; align-items: center; gap: 6px; font-size: 10.5px; }
  .vec-lab { color: var(--text-3); min-width: 44px; }
  .vec-chips { display: flex; gap: 3px; flex: 1; }
  .vec-chips button {
    flex: 1; background: var(--chip-bg); color: var(--text-2);
    border: 1px solid transparent; border-radius: 4px;
    padding: 3px 4px; font-size: 10px; cursor: pointer; font-family: inherit;
  }
  .vec-chips button.active {
    background: rgba(43, 182, 115, 0.2); color: #2bb673;
    border-color: rgba(43, 182, 115, 0.4);
  }
  .vec-slider {
    display: grid; grid-template-columns: 36px 1fr 28px;
    gap: 4px; align-items: center; font-size: 10.5px;
  }
  .vec-slider input[type='range'] { width: 100%; accent-color: #2bb673; height: 14px; }
  .vec-slider output { color: #2bb673; font-weight: 500; text-align: right; }
  .vec-toggle {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 10.5px; color: var(--text-2); cursor: pointer;
  }
  .vec-toggle input { accent-color: #2bb673; }

  .vec-actions { display: flex; gap: 4px; margin-top: 2px; }
  .vec-actions button {
    flex: 1; padding: 5px 8px; border-radius: 4px;
    font-size: 10.5px; cursor: pointer; font-family: inherit; font-weight: 500;
    display: inline-flex; align-items: center; justify-content: center; gap: 4px;
  }
  .vec-actions .primary { background: #2bb673; color: white; border: none; }
  .vec-actions .primary:hover:not(:disabled) { background: #34c684; }
  .vec-actions .primary:disabled { background: var(--chip-bg); color: var(--text-muted); cursor: not-allowed; }
  .vec-actions .ghost {
    background: var(--chip-bg); color: var(--text-2);
    border: 1px solid var(--border-soft);
  }
  .vec-actions .ghost:disabled { opacity: 0.4; cursor: not-allowed; }
  .actions button {
    flex: 1; padding: 7px 10px; border-radius: 6px;
    font-size: 11px; cursor: pointer; font-family: inherit; font-weight: 500;
    transition: background 0.12s;
  }
  .actions button.primary {
    background: var(--accent); color: white; border: none;
  }
  .actions button.primary:hover { background: var(--accent-hover); }
  .actions button.ghost {
    background: var(--chip-bg); color: var(--text-2);
    border: 1px solid var(--border-soft);
  }
  .actions button.ghost:hover { background: var(--chip-bg-hover); color: var(--text-bright); }
</style>
